import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CountryClient, CountryDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new CountryClient(API_BASE_URL, axiosInstance);

// Query hook - Get all countries for dropdowns (cached forever)
export const useCountries = () => {
  return useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const response = await apiClient.searchCountries({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useCountrySearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchCountries({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'DisplayPosition',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single country by ID
export const useCountryById = (id: string) => {
  return useQuery({
    queryKey: ['countries', id],
    queryFn: async () => {
      const response = await apiClient.getCountryById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create country
export const useCreateCountry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CountryDTO) => {
      await apiClient.createCountry(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
    },
  });
};

// Mutation hook - Update country
export const useUpdateCountry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CountryDTO }) => {
      await apiClient.updateCountry(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
    },
  });
};

// Mutation hook - Delete country
export const useDeleteCountry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteCountry(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
    },
  });
};